import { useEffect, useState, useRef, lazy, Suspense } from 'react'
const LoginPage = lazy(() => import('./pages/LoginPage'))
import { ProfileModal, useSilentRefresh } from './components/profile/ProfileModal'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CheckCircle, AlertCircle, Info, X, Keyboard,
  Columns2, List, ArrowLeft, Sun, Moon,
  LayoutDashboard, LogOut, User as UserIcon, Palette,
  ChevronUp, ChevronDown, Menu,
} from 'lucide-react'
import { useMailStore } from './stores/mailStore'
import { useUIStore } from './stores/uiStore'
import { useAccountStore } from './stores/accountStore'
import { useKeyboardShortcuts, SHORTCUTS } from './hooks/useKeyboard'
import { auth as authApi, clearAccessToken, setAccessToken, setRefreshTokenGetter } from './lib/api'
import { emailsToThreadList, buildFullThread, mailboxesToLabels } from './lib/jmapAdapter'
import { AccountSwitcher } from './components/accounts/AccountSwitcher'
import { Sidebar } from './components/layout/Sidebar'
import { MessageList } from './components/mail/MessageList'
import { ThreadView } from './components/mail/ThreadView'
import { SearchBar } from './components/search/SearchBar'
import { ComposeLayer } from './components/compose/ComposeModal'
import { WorkspaceSidebar } from './components/workspace/WorkspaceSidebar'
import { FilterCreatorModal } from './components/filters/FilterCreatorModal'
import { ThemeCustomizer, ThemeCustomizerStyles, applyStoredTheme } from './components/theme/ThemeCustomizer'
import type { Toast, Thread } from './lib/types'

// Wire the body refresh-token getter so api.ts can include it on every auto-retry.
// The httpOnly cookie alone only refreshes the primary/last-logged-in account;
// when the active account is a secondary account we need the body token too.
setRefreshTokenGetter(() => useAccountStore.getState().getActiveRefreshToken())

// ─── Toast ────────────────────────────────────────────────────────────────────

function ToastItem({ toast }: { toast: Toast }) {
  const { removeToast } = useUIStore()

  const icons = {
    success: <CheckCircle size={15} style={{ color: 'var(--green)', flexShrink: 0 }} />,
    error:   <AlertCircle size={15} style={{ color: 'var(--red)', flexShrink: 0 }} />,
    info:    <Info size={15} style={{ color: 'var(--blue)', flexShrink: 0 }} />,
  }

  return (
    <motion.div
      className="toast"
      initial={{ opacity: 0, y: 8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 4, scale: 0.96 }}
      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      layout
    >
      {icons[toast.type]}
      <span className="toast__message">{toast.message}</span>
      {toast.action && (
        <button
          className="toast__action"
          onClick={() => { toast.action!.fn(); removeToast(toast.id) }}
        >
          {toast.action.label}
        </button>
      )}
      <button className="toast__close" onClick={() => removeToast(toast.id)}>
        <X size={12} />
      </button>
    </motion.div>
  )
}

function ToastLayer() {
  const { toasts } = useUIStore()
  return (
    <div className="toast-layer">
      <AnimatePresence mode="popLayout">
        {toasts.map((t) => <ToastItem key={t.id} toast={t} />)}
      </AnimatePresence>
    </div>
  )
}

// ─── Shortcuts Modal ──────────────────────────────────────────────────────────

function ShortcutsModal() {
  const { shortcutsModalOpen, closeShortcutsModal } = useUIStore()
  return (
    <AnimatePresence>
      {shortcutsModalOpen && (
        <>
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeShortcutsModal}
          />
          <motion.div
            className="modal shortcuts-modal"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="modal__header">
              <div className="flex items-center gap-2">
                <Keyboard size={16} />
                <h2>Keyboard Shortcuts</h2>
              </div>
              <button className="btn-icon" onClick={closeShortcutsModal}>
                <X size={15} />
              </button>
            </div>
            <div className="shortcuts-grid">
              {SHORTCUTS.map((s) => (
                <div key={s.keys} className="shortcut-row">
                  <span className="shortcut-row__desc">{s.description}</span>
                  <div className="shortcut-row__keys">
                    {s.keys.split(' ').map((k, i) => (
                      <span key={i} className="kbd">{k}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// ─── Layout Toggle ────────────────────────────────────────────────────────────

function LayoutToggle() {
  const { layoutMode, setLayoutMode } = useUIStore()

  return (
    <div className="layout-toggle" title="Switch layout">
      <button
        className={`layout-toggle__btn${layoutMode === 'split' ? ' layout-toggle__btn--active' : ''}`}
        onClick={() => setLayoutMode('split')}
        title="Reading pane — message list + email side by side"
        aria-label="Reading pane on right"
      >
        <Columns2 size={15} />
      </button>
      <button
        className={`layout-toggle__btn${layoutMode === 'list' ? ' layout-toggle__btn--active' : ''}`}
        onClick={() => setLayoutMode('list')}
        title="No reading pane — open emails full width"
        aria-label="No reading pane"
      >
        <List size={15} />
      </button>
    </div>
  )
}

// ─── Theme Toggle ─────────────────────────────────────────────────────────────

function ThemeToggle() {
  const { theme, toggleTheme } = useUIStore()
  return (
    <button
      className="btn-icon"
      onClick={toggleTheme}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      aria-label="Toggle theme"
    >
      {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
    </button>
  )
}

// ─── Avatar Menu ─────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

function AvatarMenu({ onOpenCustomizer, onOpenProfile }: { onOpenCustomizer: () => void; onOpenProfile: () => void }) {
  const mailUser = useMailStore((s) => s.user)
  const { accounts, activeEmail } = useAccountStore()
  const [open, setOpen] = useState(false)
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const avatarRef = useRef<HTMLDivElement>(null)

  // Prefer the active account's user from accountStore — it updates immediately
  // on account switch, whereas mailStore.user represents the primary account.
  const activeAccount = accounts.find(a => a.email === activeEmail)
  const user = activeAccount?.user ?? mailUser

  if (!user && !activeAccount) return null

  const displayName = user?.name ?? activeAccount?.cachedName ?? activeEmail ?? ''
  const initials = displayName ? displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) : '?'
  const activeRole = user?.role ?? activeAccount?.cachedRole
  const isAdmin = activeRole === 'admin' || activeRole === 'superadmin'
  // Show account count badge when more than 1 account is loaded
  const accountCount = accounts.length

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={avatarRef}
        className="top-bar__avatar"
        title={user?.email ?? activeEmail ?? ''}
        onClick={() => { setSwitcherOpen(true); setOpen(false) }}
        style={{ cursor: 'pointer', position: 'relative' }}
        role="button"
        aria-haspopup="true"
      >
        <span style={{ fontSize: 13, fontWeight: 600 }}>{initials}</span>
        {accountCount > 1 && (
          <span style={{
            position: 'absolute', bottom: -2, right: -2,
            background: 'var(--brand)', color: '#fff',
            borderRadius: '50%', width: 14, height: 14,
            fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1.5px solid var(--bg-panel)',
          }}>{accountCount}</span>
        )}
      </div>

      <AccountSwitcher
        anchorEl={avatarRef.current}
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        onOpenProfile={onOpenProfile}
        onOpenCustomizer={onOpenCustomizer}
        isAdmin={isAdmin}
      />

      {/* Original per-account settings menu — keep for profile/theme/admin */}
      <div
        style={{ position: 'absolute', top: 0, right: 44, zIndex: 1 }}
      >
        <div
          className="top-bar__settings-btn"
          title="Settings"
          onClick={() => setOpen((v: boolean) => !v)}
          role="button"
          style={{ display: 'none' }}  // hidden — accessed via AccountSwitcher
        />
      </div>

      <AnimatePresence>
        {open && (
          <>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 900 }}
              onClick={() => setOpen(false)}
            />
            <motion.div
              className="avatar-menu"
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -4 }}
              transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* User info */}
              <div className="avatar-menu__header">
                <div className="avatar-menu__avatar">{initials}</div>
                <div>
                  <div className="avatar-menu__name">{displayName}</div>
                  <div className="avatar-menu__email">{user?.email ?? activeEmail}</div>
                  {activeRole && activeRole !== 'user' && (
                    <div className="avatar-menu__role">
                      {activeRole === 'superadmin' ? 'Super Admin' : 'Admin'}
                    </div>
                  )}
                </div>
              </div>

              <div className="avatar-menu__divider" />

              {/* Profile */}
              <button className="avatar-menu__item" onClick={() => { setOpen(false); onOpenProfile() }}>
                <UserIcon size={14} />
                <span>Profile &amp; Settings</span>
              </button>

              {/* Theme Studio */}
              <button
                className="avatar-menu__item"
                onClick={() => { setOpen(false); onOpenCustomizer() }}
              >
                <Palette size={14} />
                <span>Theme Studio</span>
              </button>

              {/* Control Panel — only for admin / superadmin */}
              {isAdmin && (
                <button
                  className="avatar-menu__item avatar-menu__item--highlight"
                  onClick={() => { window.dispatchEvent(new CustomEvent('open-cp')) }}
                >
                  <LayoutDashboard size={14} />
                  <span>Control Panel</span>
                </button>
              )}

              <div className="avatar-menu__divider" />

              <button
                className="avatar-menu__item avatar-menu__item--danger"
                onClick={async () => {
                  setOpen(false)
                  try {
                    await fetch(`${API_BASE}/auth/logout`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
                      credentials: 'include',
                    })
                  } catch { /* ignore */ }
                  clearAccessToken()
                  useMailStore.setState({ isAuthenticated: false, user: null })
                }}
              >
                <LogOut size={14} />
                <span>Sign out</span>
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style>{`
        .avatar-menu {
          position: absolute;
          top: calc(100% + 6px);
          right: 0;
          width: 220px;
          background: var(--bg-surface);
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          z-index: 1000;
          overflow: hidden;
          padding: 4px;
        }

        .avatar-menu__header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 10px 8px;
        }

        .avatar-menu__avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--brand);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 700;
          flex-shrink: 0;
        }

        .avatar-menu__name {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
          line-height: 1.3;
        }

        .avatar-menu__email {
          font-size: 11px;
          color: var(--text-tertiary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 140px;
        }

        .avatar-menu__role {
          display: inline-flex;
          margin-top: 3px;
          padding: 1px 6px;
          background: var(--brand-subtle, rgba(79,142,247,.12));
          color: var(--brand);
          border-radius: var(--radius-full);
          font-size: 10px;
          font-weight: 600;
        }

        .avatar-menu__divider {
          height: 1px;
          background: var(--border);
          margin: 3px 0;
        }

        .avatar-menu__item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 7px 10px;
          border: none;
          border-radius: var(--radius-md);
          background: transparent;
          color: var(--text-secondary);
          font-family: var(--font-ui);
          font-size: 13px;
          cursor: pointer;
          text-align: left;
          transition: background var(--duration-fast), color var(--duration-fast);
          text-decoration: none;
        }
        .avatar-menu__item:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }
        .avatar-menu__item--highlight {
          color: var(--brand);
        }
        .avatar-menu__item--highlight:hover {
          background: var(--brand-subtle, rgba(79,142,247,.1));
          color: var(--brand);
        }
        .avatar-menu__item--danger:hover {
          color: var(--red);
          background: rgba(239,68,68,.08);
        }
      `}</style>
    </div>
  )
}

// ─── Top Bar ──────────────────────────────────────────────────────────────────

function TopBar({ onOpenCustomizer, onOpenProfile }: { onOpenCustomizer: () => void; onOpenProfile: () => void }) {
  const { toggleSidebar } = useUIStore()
  return (
    <header className="top-bar">
      <div className="top-bar__left">
        {/* Hamburger: mobile only — sidebar has its own toggle on desktop */}
        <button
          className="btn-icon top-bar__hamburger"
          onClick={toggleSidebar}
          title="Toggle sidebar"
          aria-label="Toggle sidebar"
        >
          <Menu size={20} />
        </button>
      </div>
      <div className="top-bar__center">
        <SearchBar />
      </div>
      <div className="top-bar__right">
        <LayoutToggle />
        <ThemeToggle />
        <button
          className="btn-icon"
          onClick={onOpenCustomizer}
          title="Theme Studio"
          aria-label="Open Theme Studio"
        >
          <Palette size={15} />
        </button>
        <AvatarMenu onOpenCustomizer={onOpenCustomizer} onOpenProfile={onOpenProfile} />
      </div>
    </header>
  )
}

// ─── Thread Navigator (prev / next + counter) ────────────────────────────────

function ThreadNavigator({ visibleThreads }: { visibleThreads: Thread[] }) {
  const { activeThreadId, setActiveThread } = useMailStore()
  const idx = visibleThreads.findIndex((t) => t.id === activeThreadId)
  if (idx === -1 || visibleThreads.length === 0) return null

  const prev = visibleThreads[idx - 1]
  const next = visibleThreads[idx + 1]

  return (
    <div className="thread-nav">
      <span className="thread-nav__counter">
        {idx + 1} <span style={{ color: 'var(--text-tertiary)' }}>/ {visibleThreads.length}</span>
      </span>
      <button
        className="btn-icon thread-nav__btn"
        disabled={!prev}
        onClick={() => prev && setActiveThread(prev.id)}
        title="Previous conversation (k)"
      >
        <ChevronUp size={15} />
      </button>
      <button
        className="btn-icon thread-nav__btn"
        disabled={!next}
        onClick={() => next && setActiveThread(next.id)}
        title="Next conversation (j)"
      >
        <ChevronDown size={15} />
      </button>
    </div>
  )
}

// ─── Reading Pane (split mode) ────────────────────────────────────────────────

function ReadingPane({ visibleThreads }: { visibleThreads: Thread[] }) {
  const { activeThreadId, threads, setActiveThread } = useMailStore()
  const thread = threads.find((t) => t.id === activeThreadId)

  // j/k keyboard navigation within the visible list
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return
      if (!activeThreadId) return
      const idx = visibleThreads.findIndex((t) => t.id === activeThreadId)
      if (e.key === 'j' && idx < visibleThreads.length - 1) setActiveThread(visibleThreads[idx + 1].id)
      if (e.key === 'k' && idx > 0) setActiveThread(visibleThreads[idx - 1].id)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [activeThreadId, visibleThreads, setActiveThread])

  return (
    <div className="reading-pane">
      <AnimatePresence mode="wait">
        {thread ? (
          <motion.div
            key={thread.id}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Nav bar: prev/next */}
            <div className="reading-pane__nav-bar">
              <ThreadNavigator visibleThreads={visibleThreads} />
            </div>
            <ThreadView thread={thread} />
          </motion.div>
        ) : (
          <motion.div
            key="empty"
            className="reading-pane__empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="reading-pane__empty-content">
              <div className="reading-pane__empty-logo">T</div>
              <p>Select a conversation to read</p>
              <p className="text-sm text-muted">
                Press <span className="kbd">c</span> to compose,{' '}
                <span className="kbd">/</span> to search,{' '}
                <span className="kbd">?</span> for shortcuts
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── List Mode Content ────────────────────────────────────────────────────────
// "No reading pane" — exactly like Gmail: only the message list is visible.
// Clicking a thread slides in the full-width reading view with a ← Back button.
// Works identically on desktop and mobile.

function ListModeContent({ threads: visibleThreads }: { threads: Thread[] }) {
  const { activeThreadId, threads, setActiveThread } = useMailStore()
  const thread = threads.find((t) => t.id === activeThreadId)

  return (
    <div className="gm-layout gm-layout--stack">
      <AnimatePresence mode="wait" initial={false}>
        {thread ? (
          <motion.div
            key="reading"
            className="gm-stack-pane"
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Back + navigator bar */}
            <div className="gm-back-bar">
              <button
                className="gm-back-btn"
                onClick={() => setActiveThread(null)}
                aria-label="Back to list"
              >
                <ArrowLeft size={16} />
                <span>Back</span>
              </button>
              <ThreadNavigator visibleThreads={visibleThreads} />
            </div>
            <ThreadView thread={thread} />
          </motion.div>
        ) : (
          <motion.div
            key="list"
            className="gm-stack-pane"
            initial={{ x: '-30%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-30%', opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            <MessageList threads={visibleThreads} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Main App ────────────────────────────────────────────────────────────────

export default function App() {
  const { threads, activeFolder, activeThreadId, openCompose, isAuthenticated } = useMailStore()
  const { layoutMode, theme, density } = useUIStore()
  // Reactive: re-render when the active account's JMAP mailboxes arrive (async after login)
  const activeMailboxCount = useAccountStore(s => s.accounts.find(a => a.email === s.activeEmail)?.mailboxes.length ?? 0)
  const activeEmail = useAccountStore(s => s.activeEmail)
  // Re-run push effect when any account finishes connecting its JMAP client
  const connectedClientCount = useAccountStore(s => s.accounts.filter(a => a.client !== null).length)

  // On mobile both layout modes behave as a stack (list → thread)
  const [isMobileApp, setIsMobileApp] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const update = (e: MediaQueryListEvent) => setIsMobileApp(e.matches)
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  const [showCustomizer, setShowCustomizer] = useState(false)
  const [showProfile, setShowProfile] = useState(false)
  const [authChecked, setAuthChecked] = useState(false)

  // Register keyboard shortcuts globally
  useKeyboardShortcuts()

  // Silent token refresh — keeps user logged in like Gmail
  useSilentRefresh()

  // Bootstrap: load mailboxes (= labels) + inbox threads via JMAP after authentication.
  // Depends on activeMailboxCount so it re-fires once addAccount() finishes loading
  // mailboxes asynchronously (avoids the blank-sidebar race on first login).
  useEffect(() => {
    if (!isAuthenticated) return
    const client = useAccountStore.getState().getActiveClient()
    if (!client) return  // JMAP client not yet connected (addAccount is async)

    const mailboxes = useAccountStore.getState().getActiveMailboxes()
    if (mailboxes.length === 0) return  // mailboxes not fetched yet

    const inboxMb = client.findMailboxByRole(mailboxes, 'inbox')
    if (!inboxMb) return

    useMailStore.setState({ isSyncing: true, labels: mailboxesToLabels(mailboxes) })
    client.listThreads(inboxMb.id, { limit: 50 })
      .then(({ emails }) => {
        useMailStore.setState({ threads: emailsToThreadList(emails, mailboxes) })
      })
      .catch(() => {})
      .finally(() => useMailStore.setState({ isSyncing: false }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeMailboxCount])

  // Sync theme to <html data-theme="..."> and restore saved theme vars
  useEffect(() => {
    document.documentElement.dataset.theme = theme
    applyStoredTheme()
  }, [theme])

  // Apply density to <html data-density="...">
  useEffect(() => {
    document.documentElement.dataset.density = density
  }, [density])

  // On startup: validate stored token, restore JMAP client, reconnect other accounts
  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) { setAuthChecked(true); return }

    const apiBase = import.meta.env.VITE_API_URL ?? ''

    // Proactively refresh if the stored token is already expired so we never
    // send a request we know will 401.
    const isExpired = (t: string): boolean => {
      try {
        const payload = JSON.parse(atob(t.split('.')[1]))
        return typeof payload.exp === 'number' && payload.exp * 1000 < Date.now()
      } catch { return true }
    }

    // Decode the JWT email claim so we can look up the right body refresh token.
    // Every "Add another account" login overwrites the httpOnly refresh cookie,
    // so we must use the per-account body token stored in tmail_accounts instead.
    const getJwtEmail = (t: string): string => {
      try { return JSON.parse(atob(t.split('.')[1])).email ?? '' } catch { return '' }
    }

    const refreshToken = (expiredToken: string): Promise<string> => {
      const email = getJwtEmail(expiredToken)
      const persisted = useAccountStore.getState().getPersistedAccounts()
      const bodyRT = email ? (persisted.find(a => a.email === email)?.refreshToken ?? '') : ''
      return fetch(`${apiBase}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        ...(bodyRT ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh_token: bodyRT }) } : {}),
      }).then(r => r.ok ? r.json() : Promise.reject()).then(d => d.access_token)
    }

    const bootstrap = (freshToken: string) => {
      setAccessToken(freshToken)
      // Validate JWT and get JMAP credentials in parallel
      return Promise.all([
        authApi.me(),
        authApi.jmapCredentials(),
      ])
    }

    const start = isExpired(token)
      ? refreshToken(token).then(bootstrap)
      : bootstrap(token)

    start
      .then(async ([user, creds]) => {
        useMailStore.setState({ user, isAuthenticated: true })
        // Reconnect primary JMAP client and make it the active account.
        // Preserve any refresh token that was previously stored for this email
        // in tmail_accounts (e.g. from a prior login where it was a secondary account,
        // or from the initial LoginPage login that persisted it in the body response).
        // Passing '' would overwrite the stored token and cause the account to be
        // removed from the switcher once the JWT expires and it becomes secondary.
        const persistedAccounts = useAccountStore.getState().getPersistedAccounts()
        const storedEntry = persistedAccounts.find(a => a.email === user.email)
        await useAccountStore.getState().addAccount(
          user.email,
          token,
          storedEntry?.refreshToken ?? '',
          creds.jmap_url,
          creds.password,
          user,
        )
        useAccountStore.getState().setActiveAccount(user.email)

        // Reconnect other persisted accounts in the background using their stored JWTs.
        // Skip accounts whose JWT is already expired (avoids 401 console noise).
        const isJwtExpired = (token: string): boolean => {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]))
            return typeof payload.exp === 'number' && payload.exp * 1000 < Date.now()
          } catch { return true }
        }
        const others = useAccountStore.getState().getPersistedAccounts()
          .filter(a => a.email !== user.email && a.accessToken)
        others.forEach(async (stored) => {
          // If the stored access token is expired, try refreshing it with the
          // persisted refresh token before giving up. Access tokens are only
          // 15 min; refresh tokens are 30 days. The backend accepts refresh
          // tokens in the request body (not cookie-only) for exactly this case.
          let accessToken = stored.accessToken
          if (isJwtExpired(accessToken)) {
            if (!stored.refreshToken) {
              // No refresh token available — skip reconnecting but do NOT remove.
              // The account may have been stored as the primary account (which uses
              // an httpOnly cookie instead of a body refresh token). Removing it
              // silently would cause it to disappear from the account switcher.
              return
            }
            try {
              const r = await fetch(`${apiBase}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: stored.refreshToken }),
              })
              if (!r.ok) { useAccountStore.getState().removeAccount(stored.email); return }
              const data = await r.json()
              accessToken = data.access_token
              // Persist the fresh token so the next reload starts clean
              useAccountStore.getState().updateAccount(stored.email, { accessToken })
            } catch {
              useAccountStore.getState().removeAccount(stored.email)
              return
            }
          }
          const h = { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
          Promise.all([
            fetch(`${apiBase}/auth/me`, { headers: h, credentials: 'include' })
              .then(r => { if (!r.ok) { if (r.status === 401) useAccountStore.getState().removeAccount(stored.email); return Promise.reject() } return r.json() }),
            fetch(`${apiBase}/auth/jmap-credentials`, { headers: h, credentials: 'include' })
              .then(r => { if (!r.ok) { if (r.status === 401) useAccountStore.getState().removeAccount(stored.email); return Promise.reject() } return r.json() }),
          ])
            .then(([otherUser, otherCreds]) =>
              useAccountStore.getState().addAccount(
                stored.email, accessToken, stored.refreshToken, stored.jmapUrl,
                otherCreds.password, otherUser,
              )
            )
            .catch(() => { /* unrecoverable — account removed from persistence above */ })
        })
      })
      .catch(() => {
        clearAccessToken()
        useMailStore.setState({ user: null, isAuthenticated: false })
      })
      .finally(() => setAuthChecked(true))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-fetch threads via JMAP when the active folder changes
  useEffect(() => {
    if (!isAuthenticated) return
    const client = useAccountStore.getState().getActiveClient()
    const mailboxes = useAccountStore.getState().getActiveMailboxes()
    if (!client || mailboxes.length === 0) return

    // Map our folder slug to a JMAP mailbox.
    // Some slugs differ from JMAP roles (e.g. "spam" → role "junk").
    const SLUG_TO_ROLE: Record<string, string> = { spam: 'junk' }
    const jmapRole = SLUG_TO_ROLE[activeFolder] ?? activeFolder
    const mb = client.findMailboxByRole(mailboxes, jmapRole)
      ?? client.findMailboxByName(mailboxes, activeFolder)
    if (!mb) return

    useMailStore.setState({ isSyncing: true })
    client.listThreads(mb.id, { limit: 50 })
      .then(({ emails }) => useMailStore.setState({ threads: emailsToThreadList(emails, mailboxes) }))
      .catch(() => {})
      .finally(() => useMailStore.setState({ isSyncing: false }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolder, isAuthenticated])

  // Fetch full thread (with message bodies) via JMAP when a thread is opened
  useEffect(() => {
    if (!isAuthenticated || !activeThreadId) return
    const existing = useMailStore.getState().threads.find((t) => t.id === activeThreadId)
    if (existing?.messages && existing.messages.length > 0) return
    const client = useAccountStore.getState().getActiveClient()
    const mailboxes = useAccountStore.getState().getActiveMailboxes()
    if (!client) return
    client.getThread(activeThreadId)
      .then(({ thread: jmapThread, emails }) => {
        useMailStore.setState((state) => ({
          threads: state.threads.map((t) =>
            t.id === activeThreadId && existing
              ? buildFullThread(existing, jmapThread, emails, mailboxes)
              : t
          ),
        }))

        // Mark all unread emails in the thread as $seen
        const unreadIds = emails
          .filter(e => !('$seen' in (e.keywords ?? {})))
          .map(e => e.id)
        if (unreadIds.length > 0) {
          // Update local thread state immediately so the unread dot disappears
          useMailStore.getState().markRead(activeThreadId, true)
          // Tell Stalwart, then re-fetch mailboxes to get updated unread counts
          client.setKeyword(unreadIds, '$seen', true)
            .then(() => client.listMailboxes())
            .then(updatedMailboxes => {
              useAccountStore.getState().updateAccount(client.accountEmail, { mailboxes: updatedMailboxes })
              useMailStore.setState({ labels: mailboxesToLabels(updatedMailboxes) })
            })
            .catch(() => {})
        }
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId, isAuthenticated])

  // When JMAP client finishes connecting (async), re-trigger the bootstrap
  useEffect(() => {
    if (!isAuthenticated) return
    // Subscribe to accountStore changes — when mailboxes arrive, re-fetch inbox
    return useAccountStore.subscribe(
      (state) => state.getActiveMailboxes().length,
      (mailboxCount) => {
        if (mailboxCount === 0) return
        const client = useAccountStore.getState().getActiveClient()
        const mailboxes = useAccountStore.getState().getActiveMailboxes()
        if (!client) return
        const inboxMb = client.findMailboxByRole(mailboxes, 'inbox')
        if (!inboxMb) return
        useMailStore.setState({ isSyncing: true, labels: mailboxesToLabels(mailboxes) })
        client.listThreads(inboxMb.id, { limit: 50 })
          .then(({ emails }) => useMailStore.setState({ threads: emailsToThreadList(emails, mailboxes), activeFolder: 'inbox' }))
          .catch(() => {})
          .finally(() => useMailStore.setState({ isSyncing: false }))
      }
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  // Re-fetch when the user switches accounts
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      const { email } = e.detail as { email: string }
      const client = useAccountStore.getState().accounts.find(a => a.email === email)?.client
      const mailboxes = useAccountStore.getState().accounts.find(a => a.email === email)?.mailboxes ?? []
      if (!client || mailboxes.length === 0) return
      const inboxMb = client.findMailboxByRole(mailboxes, 'inbox')
      if (!inboxMb) return
      useMailStore.setState({ isSyncing: true, threads: [], labels: mailboxesToLabels(mailboxes), activeFolder: 'inbox', activeThreadId: null })
      client.listThreads(inboxMb.id, { limit: 50 })
        .then(({ emails }) => useMailStore.setState({ threads: emailsToThreadList(emails, mailboxes) }))
        .catch(() => {})
        .finally(() => useMailStore.setState({ isSyncing: false }))
    }
    window.addEventListener('account:switched', handler as EventListener)
    return () => window.removeEventListener('account:switched', handler as EventListener)
  }, [])

  // JMAP push — connect EventSource for every logged-in account, refresh view on state change
  useEffect(() => {
    if (!isAuthenticated) return
    const { accounts, activeEmail } = useAccountStore.getState()

    accounts.forEach(account => {
      const { client } = account
      if (!client || client.isPushConnected()) return

      client.connectPush((change) => {
        const accountId = client.accountId
        if (!change.changed[accountId]?.Email) return

        // Only update the visible thread list when this account is active
        if (account.email !== useAccountStore.getState().activeEmail) return

        const mailboxes = useAccountStore.getState().getActiveMailboxes()
        if (mailboxes.length === 0) return
        const { activeFolder } = useMailStore.getState()
        const SLUG_TO_ROLE: Record<string, string> = { spam: 'junk' }
        const jmapRole = SLUG_TO_ROLE[activeFolder] ?? activeFolder
        const mb = client.findMailboxByRole(mailboxes, jmapRole)
          ?? client.findMailboxByName(mailboxes, activeFolder)
        if (!mb) return
        client.listThreads(mb.id, { limit: 50 })
          .then(({ emails }) => {
            const newThreads = emailsToThreadList(emails, mailboxes)
            // Preserve already-fetched message bodies so the detail view doesn't
            // go blank for the currently-open thread when push refreshes the list.
            useMailStore.setState(state => ({
              threads: newThreads.map(newThread => {
                const prev = state.threads.find(t => t.id === newThread.id)
                return prev?.messages?.length ? { ...newThread, messages: prev.messages } : newThread
              }),
            }))
          })
          .catch(() => {})
      })
    })

    return () => {
      useAccountStore.getState().accounts.forEach(a => a.client?.disconnectPush())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeMailboxCount, activeEmail, connectedClientCount])

  // Listen for auth logout events from the API client
  useEffect(() => {
    const handler = () => {
      clearAccessToken()
      useMailStore.setState({ isAuthenticated: false, user: null })
    }
    window.addEventListener('auth:logout', handler)
    return () => window.removeEventListener('auth:logout', handler)
  }, [])

  // Show nothing while we verify the stored token (avoids flash)
  if (!authChecked && !isAuthenticated) return null

  // Show login page when not authenticated
  if (!isAuthenticated) {
    return (
      <Suspense fallback={null}>
        <LoginPage />
      </Suspense>
    )
  }

  // Filter threads by active folder — handles both label-based and special virtual folders
  const visibleThreads = threads.filter((t) => {
    switch (activeFolder) {
      case 'starred':   return t.is_starred
      case 'important': return t.is_important
      case 'snoozed':   return !!(t.snoozed_until && new Date(t.snoozed_until) > new Date())
      case 'all_mail':  return !t.labels.some((l) => l.slug === 'trash' || l.slug === 'spam')
      default:          return t.labels.some((l) => l.slug === activeFolder)
    }
  })

  return (
    <div className="app-shell">
      {/* Sidebar — overlay drawer on mobile, collapsible on desktop */}
      <Sidebar onCompose={() => openCompose()} onOpenCustomizer={() => setShowCustomizer(true)} />

      {/* Main panel */}
      <div className="app-main">
        <TopBar onOpenCustomizer={() => setShowCustomizer(true)} onOpenProfile={() => setShowProfile(true)} />
        <div className="app-content">
          {/* split  = reading pane right: message list + email side by side (desktop only).
              list   = no reading pane: full-width stack navigation, same on mobile & desktop. */}
          {!isMobileApp && layoutMode === 'split' ? (
            <>
              <MessageList threads={visibleThreads} />
              <ReadingPane visibleThreads={visibleThreads} />
            </>
          ) : (
            /* list mode on desktop + all mobile: full-width stack (no reading pane) */
            <ListModeContent threads={visibleThreads} />
          )}
        </div>
      </div>

      {/* Right workspace sidebar (Calendar / Tasks / Notes / Contacts) */}
      <WorkspaceSidebar />

      {/* Overlays */}
      <ComposeLayer />
      <ToastLayer />
      <ShortcutsModal />
      <FilterCreatorModal />

      {/* Profile & Settings */}
      <ProfileModal open={showProfile} onClose={() => setShowProfile(false)} />

      {/* Theme Studio */}
      <ThemeCustomizer open={showCustomizer} onClose={() => setShowCustomizer(false)} />
      <ThemeCustomizerStyles />

      <style>{`
        .app-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          min-width: 0;
        }

        .top-bar {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 8px 16px 8px 16px;
          border-bottom: 1px solid var(--border);
          min-height: 52px;
          flex-shrink: 0;
          background: var(--bg-panel);
          justify-content: space-between;
        }

        .top-bar__left {
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }

        .top-bar__hamburger {
          display: none; /* hidden on desktop */
        }

        @media (max-width: 768px) {
          .top-bar__hamburger {
            display: flex;
            width: 36px;
            height: 36px;
            color: var(--text-secondary);
          }
        }

        .top-bar__center {
          flex: 1;
          display: flex;
          justify-content: center;
          padding: 0 8px;
        }

        .top-bar__right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .top-bar__avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--brand);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
        }

        .app-content {
          flex: 1;
          display: flex;
          overflow: hidden;
        }

        .reading-pane {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .reading-pane__nav-bar {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding: 4px 10px;
          border-bottom: 1px solid var(--border);
          min-height: 36px;
          flex-shrink: 0;
          background: var(--bg-panel);
        }

        /* Thread navigator (shared between split + list mode) */
        .thread-nav {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .thread-nav__counter {
          font-size: 11px;
          font-family: var(--font-mono);
          color: var(--text-secondary);
          margin-right: 4px;
          white-space: nowrap;
        }

        .thread-nav__btn {
          padding: 4px;
          color: var(--text-tertiary);
        }

        .thread-nav__btn:disabled {
          opacity: 0.25;
          cursor: not-allowed;
        }

        .thread-nav__btn:not(:disabled):hover {
          color: var(--text-primary);
          background: var(--bg-hover);
        }

        .reading-pane__empty {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
        }

        .reading-pane__empty-content {
          text-align: center;
          color: var(--text-tertiary);
          font-size: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .reading-pane__empty-logo {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          background: var(--bg-elevated);
          border: 1px solid var(--border);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 700;
          color: var(--text-tertiary);
          margin-bottom: 8px;
        }

        /* Toast layer */
        .toast-layer {
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column-reverse;
          gap: 8px;
          z-index: 2000;
          pointer-events: none;
          align-items: center;
        }

        .toast {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          pointer-events: all;
          max-width: 400px;
          min-width: 220px;
        }

        .toast__message {
          flex: 1;
          font-size: 13px;
          color: var(--text-primary);
        }

        .toast__action {
          background: none;
          border: none;
          cursor: pointer;
          color: var(--brand);
          font-family: var(--font-ui);
          font-size: 12px;
          font-weight: 600;
          padding: 2px 4px;
          border-radius: 3px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .toast__action:hover { background: var(--brand-subtle); }

        .toast__close {
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-tertiary);
          display: flex;
          align-items: center;
          padding: 2px;
          flex-shrink: 0;
        }
        .toast__close:hover { color: var(--text-primary); }

        /* Layout toggle */
        .layout-toggle {
          display: flex;
          align-items: center;
          gap: 2px;
          background: var(--bg-elevated);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: 2px;
        }

        .layout-toggle__btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 26px;
          border: none;
          border-radius: calc(var(--radius-md) - 2px);
          background: transparent;
          color: var(--text-tertiary);
          cursor: pointer;
          transition: background var(--duration-fast), color var(--duration-fast);
        }

        .layout-toggle__btn:hover {
          color: var(--text-primary);
          background: var(--bg-hover);
        }

        .layout-toggle__btn--active {
          background: var(--bg-selected-strong);
          color: var(--text-primary);
        }

        /* Gmail dual-panel layout (list mode) */
        .gm-layout {
          flex: 1;
          display: flex;
          flex-direction: row;
          overflow: hidden;
        }

        /* Mobile: single-panel stack navigation */
        .gm-layout--mobile {
          position: relative;
          overflow: hidden;
        }

        .gm-mobile-pane {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: var(--bg-panel);
        }

        .gm-mobile-back-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 8px 6px 4px;
          border-bottom: 1px solid var(--border);
          flex-shrink: 0;
          background: var(--bg-panel);
          min-height: 44px;
        }

        .gm-mobile-back-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border: none;
          border-radius: var(--radius-md);
          background: transparent;
          color: var(--text-secondary);
          font-family: var(--font-ui);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: background var(--duration-fast), color var(--duration-fast);
        }
        .gm-mobile-back-btn:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        @media (max-width: 768px) {
          .app-shell {
            overflow: hidden;
          }
        }

        .gm-list {
          flex-shrink: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          border-right: 1px solid var(--border);
        }

        .gm-divider {
          width: 4px;
          flex-shrink: 0;
          cursor: col-resize;
          background: transparent;
          transition: background 0.15s;
          position: relative;
          z-index: 1;
          margin: 0 -2px;
        }

        .gm-divider:hover,
        .gm-divider:active {
          background: var(--brand);
          opacity: 0.5;
        }

        .gm-reading {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .gm-reading__nav {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding: 4px 10px;
          border-bottom: 1px solid var(--border);
          min-height: 36px;
          flex-shrink: 0;
          background: var(--bg-panel);
        }

        /* Stack (no-reading-pane) mode — list → full-width thread, with Back button */
        .gm-layout--stack {
          flex: 1;
          position: relative;
          overflow: hidden;
        }

        .gm-stack-pane {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: var(--bg-panel);
        }

        /* MessageList inside stack must be full width */
        .gm-layout--stack .message-list-container {
          width: 100% !important;
          flex: 1;
        }

        .gm-back-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 12px 6px 8px;
          border-bottom: 1px solid var(--border);
          flex-shrink: 0;
          background: var(--bg-panel);
          min-height: 44px;
        }

        .gm-back-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border: none;
          border-radius: var(--radius-md);
          background: transparent;
          color: var(--text-secondary);
          font-family: var(--font-ui);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: background var(--duration-fast), color var(--duration-fast);
        }

        .gm-back-btn:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        /* Modal */
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.6);
          z-index: 1500;
        }

        .modal {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 1600;
          background: var(--bg-surface);
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-xl);
          min-width: 400px;
          max-width: 520px;
          overflow: hidden;
        }

        .modal__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border);
        }

        .modal__header h2 {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .shortcuts-grid {
          padding: 12px 20px 20px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          max-height: 70vh;
          overflow-y: auto;
        }

        .shortcut-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 7px 8px;
          border-radius: var(--radius-sm);
        }
        .shortcut-row:hover { background: var(--bg-hover); }

        .shortcut-row__desc {
          font-size: 13px;
          color: var(--text-secondary);
        }

        .shortcut-row__keys {
          display: flex;
          align-items: center;
          gap: 3px;
        }
      `}</style>
    </div>
  )
}
